import React, { useState, createElement, Component } from 'react';
import { CopyIcon, DownloadIcon, CheckIcon } from 'lucide-react';
export function StyleGuideTab() {
  const [copiedHex, setCopiedHex] = useState<string | null>(null);
  const colors = [{
    name: 'Primary Navy',
    hex: '#1a2744'
  }, {
    name: 'Accent Pink',
    hex: '#e91e8c'
  }, {
    name: 'Secondary Blue',
    hex: '#5bbcd5'
  }, {
    name: 'Dark Slate',
    hex: '#2b3545'
  }, {
    name: 'Light Gray',
    hex: '#f8f9fa'
  }, {
    name: 'Text Gray',
    hex: '#8494a7'
  }];
  const typography = [{
    tag: 'h1',
    family: 'Bebas Neue',
    size: 'text-7xl to text-[11rem]',
    weight: 'Normal',
    usage: 'Hero Headers'
  }, {
    tag: 'h2',
    family: 'Bebas Neue / Poppins',
    size: 'text-3xl to text-8xl',
    weight: 'Bold/Normal',
    usage: 'Section Headers'
  }, {
    tag: 'h3',
    family: 'Poppins',
    size: 'text-xl to text-2xl',
    weight: 'Bold',
    usage: 'Card Titles'
  }, {
    tag: 'p',
    family: 'Poppins',
    size: 'text-sm to text-base',
    weight: 'Normal',
    usage: 'Body Text'
  }, {
    tag: 'accent',
    family: 'Playfair Display',
    size: 'Various',
    weight: 'Italic',
    usage: 'Decorative Text'
  }];
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedHex(text);
    setTimeout(() => setCopiedHex(null), 1500);
  };
  const handleExportCSS = () => {
    const css = `/* ============================================
   Smith & Jengs — Design System Style Guide
   Generated: ${new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })}
   ============================================ */

/* ---- Color Tokens ---- */
:root {
  --color-primary-navy:    #1a2744;
  --color-accent-pink:     #e91e8c;
  --color-secondary-blue:  #5bbcd5;
  --color-dark-slate:      #2b3545;
  --color-light-gray:      #f8f9fa;
  --color-text-gray:       #8494a7;
  --color-pink-hover:      #d0187c;
  --color-cream:           #f5f0e8;
}

/* ---- Font Families ---- */
/* Primary sans-serif */
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
/* Display / headings */
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');
/* Serif accent */
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&display=swap');

:root {
  --font-sans:      'Poppins', sans-serif;
  --font-display:   'Bebas Neue', sans-serif;
  --font-serif:     'Playfair Display', serif;
}

/* ---- Typography Scale ---- */
/* h1 — Hero Headers */
h1 {
  font-family: var(--font-display);
  font-weight: 400;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  /* Responsive: 4.5rem → 11rem */
}

/* h2 — Section Headers */
h2 {
  font-family: var(--font-display);
  font-weight: 400;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  /* Responsive: 1.875rem → 8xl */
}

/* h3 — Card / Subsection Titles */
h3 {
  font-family: var(--font-sans);
  font-weight: 700;
  font-size: 1.25rem; /* text-xl */
  line-height: 1.4;
  color: var(--color-primary-navy);
}

/* Body text */
p, .body-text {
  font-family: var(--font-sans);
  font-weight: 400;
  font-size: 0.875rem; /* text-sm to text-base */
  line-height: 1.625;
  color: #4b5563; /* gray-600 */
}

/* Decorative / accent text */
.accent-text {
  font-family: var(--font-serif);
  font-style: italic;
}

/* ---- Spacing Tokens ---- */
:root {
  --spacing-section-y:     4rem;    /* py-16 */
  --spacing-section-y-lg:  6rem;    /* md:py-24 */
  --spacing-page-x:        1.5rem;  /* px-6 */
  --spacing-page-x-lg:     3rem;    /* md:px-12 */
  --spacing-container-max: 80rem;   /* max-w-7xl */
}

/* ---- Border & Radius ---- */
:root {
  --radius-sm:    0.125rem;  /* rounded-sm */
  --radius-xl:    0.75rem;   /* rounded-xl */
  --radius-full:  9999px;    /* rounded-full (buttons) */
  --border-light: #e5e7eb;   /* gray-200 */
  --border-mid:   #d1d5db;   /* gray-300 */
}

/* ---- Component Patterns ---- */
.btn-primary {
  background-color: var(--color-accent-pink);
  color: #ffffff;
  padding: 1rem 2rem;
  border-radius: var(--radius-full);
  font-weight: 600;
  letter-spacing: 0.05em;
  transition: background-color 0.2s;
}
.btn-primary:hover {
  background-color: var(--color-pink-hover);
}

.section-container {
  max-width: var(--spacing-container-max);
  margin-left: auto;
  margin-right: auto;
  padding-left: var(--spacing-page-x);
  padding-right: var(--spacing-page-x);
}

@media (min-width: 768px) {
  .section-container {
    padding-left: var(--spacing-page-x-lg);
    padding-right: var(--spacing-page-x-lg);
  }
}
`;
    const blob = new Blob([css], {
      type: 'text/css'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'smith-jengs-style-guide.css';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return <div className="space-y-8 p-4 pb-24 text-sm text-slate-300" data-id="element-203">
      {/* Colors */}
      <section data-id="element-204">
        <div className="flex items-center justify-between mb-4" data-id="element-205">
          <h3 className="font-semibold text-white" data-id="element-206">Colors</h3>
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-207">
          {colors.map(color => <div key={color.hex} className="bg-slate-800 rounded-md p-2 border border-slate-700" data-id="element-208">
              <div className="w-full h-12 rounded-sm mb-2 shadow-inner" style={{
            backgroundColor: color.hex
          }} data-id="element-209" />
              <div className="flex items-center justify-between" data-id="element-210">
                <div data-id="element-211">
                  <div className="text-xs font-medium text-slate-200" data-id="element-212">
                    {color.name}
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono" data-id="element-213">
                    {color.hex}
                  </div>
                </div>
                <button onClick={() => copyToClipboard(color.hex)} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-colors flex items-center gap-1" title="Copy HEX" data-id="element-214">
                  {copiedHex === color.hex ? <span className="text-[10px] text-emerald-400 flex items-center gap-1" data-id="element-215">
                      <CheckIcon className="w-3 h-3" data-id="element-216" /> Copied
                    </span> : <CopyIcon className="w-3 h-3" data-id="element-217" />}
                </button>
              </div>
            </div>)}
        </div>
      </section>

      {/* Typography */}
      <section data-id="element-218">
        <h3 className="font-semibold text-white mb-4" data-id="element-219">Typography</h3>
        <div className="space-y-3" data-id="element-220">
          {typography.map(type => <div key={type.tag} className="bg-slate-800 rounded-md p-3 border border-slate-700" data-id="element-221">
              <div className="flex items-center justify-between mb-1" data-id="element-222">
                <span className="font-mono text-xs text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded" data-id="element-223">
                  &lt;{type.tag}&gt;
                </span>
                <span className="text-xs text-slate-400" data-id="element-224">{type.usage}</span>
              </div>
              <div className="text-slate-200 font-medium" data-id="element-225">{type.family}</div>
              <div className="text-xs text-slate-400 mt-1 flex gap-3" data-id="element-226">
                <span data-id="element-227">{type.weight}</span>
                <span data-id="element-228">•</span>
                <span data-id="element-229">{type.size}</span>
              </div>
            </div>)}
        </div>
      </section>

      {/* Spacing & Radius */}
      <section data-id="element-230">
        <h3 className="font-semibold text-white mb-4" data-id="element-231">Spacing & Radius</h3>
        <div className="bg-slate-800 rounded-md p-3 border border-slate-700 space-y-2" data-id="element-232">
          <div className="flex justify-between items-center border-b border-slate-700 pb-2" data-id="element-233">
            <span className="text-slate-400" data-id="element-234">Section Padding</span>
            <span className="font-mono text-xs text-slate-200" data-id="element-235">
              py-16 md:py-24
            </span>
          </div>
          <div className="flex justify-between items-center border-b border-slate-700 pb-2 pt-1" data-id="element-236">
            <span className="text-slate-400" data-id="element-237">Container Max</span>
            <span className="font-mono text-xs text-slate-200" data-id="element-238">max-w-7xl</span>
          </div>
          <div className="flex justify-between items-center pt-1" data-id="element-239">
            <span className="text-slate-400" data-id="element-240">Border Radius</span>
            <span className="font-mono text-xs text-slate-200" data-id="element-241">
              rounded-sm / rounded-xl
            </span>
          </div>
        </div>
      </section>

      <button onClick={handleExportCSS} className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center justify-center gap-2 transition-colors font-medium" data-id="element-242">
        <DownloadIcon className="w-4 h-4" data-id="element-243" />
        Export Style Guide (.css)
      </button>
    </div>;
}